/**
 * ChatPanel — NORA, the in-app "Station Operator" chat overlay.
 *
 * Opens on the `meridian:open-chat` window event (dispatched by the Instrument
 * Bezel) or Cmd/Ctrl+J. Streams answers from POST /api/chat over SSE and renders
 * assistant text, transient tool-status chips, and deep-link buttons parsed from
 * the assistant's fenced ```links block. Mounted as a sibling of CommandPalette
 * in App.tsx (inside BrowserRouter/UserSessionProvider) so navigation works.
 *
 * Conversations live in the durable chatSessionStore, not local state: the panel
 * holds MANY sessions at once (a browser-like strip of tabs) and keeps every past
 * conversation as browsable HISTORY across reloads. The component owns only the
 * ephemeral streaming state (in-flight tokens, tool status, errors) — that
 * describes a request, not a conversation. Streaming is bound to the session it
 * started in (streamSessionRef), so tokens land correctly even if the user
 * switches tabs mid-answer.
 *
 * Verification is jsdom-only in this repo (see the sandbox-no-browser-verification
 * memory) — the SSE reader, message rendering, link parsing, navigation, and the
 * tabs/history behavior are covered by ChatPanel.test.tsx with a mocked fetch.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStationDataStore } from '../../store/stationDataStore';
import {
  useChatSessionStore,
  selectActiveSession,
  selectOpenTabs,
  selectHistory,
} from '../../store/chatSessionStore';
import { ObservationsAPI } from '../../services/ObservationsAPI';
import { parseLinks, parseFollowups, consumeSse, chipLabel } from './chatSse';
import { pickStarters, relTime } from './chatStarters';
import { renderMarkdown } from './chatMarkdown';
import './ChatPanel.css';

const MAX_HISTORY_TURNS = 10; // keep POST body well under the server's 1MB cap

// How long the green "done" pulse lingers on a tab after a turn finishes before
// the dot goes quiet — long enough to notice a background answer landed, short
// enough that answered tabs don't blink forever.
const DONE_PULSE_MS = 4000;

// How long the just-sent user bubble plays its "rise into the thread" entrance
// before the transient marker class is dropped. Matches the CSS animation
// duration; clearing it means the entrance never replays on a tab-switch or
// reload (the bubble is only "sending" for this one moment).
const SEND_ANIM_MS = 460;

// How long a history row plays its "pop" (inflate → burst + collapse) before the
// conversation is actually removed from the store. Matches the --duration-slow
// (350ms) used by the .chat-history-row.is-leaving CSS so the row is removed the
// instant the motion ends.
const LEAVE_ANIM_MS = 350;

// The status a tab's blinking dot conveys. Only one stream runs at a time, but a
// background tab can still be mid-answer, freshly-done, or holding an error while
// the user looks at another conversation — so status is computed per tab.
type TabStatus = 'running' | 'done' | 'error' | 'idle';

// Prime the hover marquee on a tab title: measure how far the (clipped) text
// overflows its button and stash that as the CSS custom property the keyframe
// slides by, keeping the scroll speed roughly constant regardless of length. Runs
// on mouseenter so it always reflects the live layout; a no-op in jsdom (layout
// metrics read 0), which is why the tests don't exercise it.
function primeTabMarquee(e: React.MouseEvent<HTMLButtonElement>) {
  const btn = e.currentTarget;
  const text = btn.querySelector<HTMLElement>('.chat-tab-label-text');
  if (!text) return;
  // Measure the FULL title width against the button's clip box — not the span's
  // own clientWidth. Reading a layout metric forces a style flush that applies the
  // pending :hover rule (which grows the span to width:max-content), so the span's
  // clientWidth would already read as expanded → overflow 0 → no marquee. The
  // button's clip box (fixed max-width, overflow:hidden) is unaffected by :hover.
  // text.scrollWidth is the full content width in both states.
  const style = getComputedStyle(btn);
  const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  const maskWidth = btn.clientWidth - padX;
  const overflow = text.scrollWidth - maskWidth;
  if (overflow > 1) {
    text.style.setProperty('--marquee-shift', `${-overflow}px`);
    // One slow, deliberate reveal: ~18px/s, clamped so even a short overflow still
    // eases across (not an instant jump) and a very long title doesn't crawl.
    text.style.animationDuration = `${Math.min(7, Math.max(2.2, overflow / 18)).toFixed(2)}s`;
  } else {
    text.style.setProperty('--marquee-shift', '0px');
  }
}

export const ChatPanel = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  // True only while assistant tokens are actively flowing. Goes false at the
  // start of a turn and after each tool finishes, so the "thinking" indicator can
  // reappear during every quiet gap (initial wait, tool execution, and the pause
  // before the next answer segment starts) — not just before the first token.
  const [receiving, setReceiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The session an error belongs to. An error raised by a background stream
  // (the user switched tabs mid-answer) must surface under ITS conversation, not
  // whichever tab is on screen — the banner render is gated on this === activeId.
  const [errorSessionId, setErrorSessionId] = useState<string | null>(null);
  // The session whose last turn just completed successfully. Drives a brief green
  // "done" pulse on that tab's dot, then self-clears after DONE_PULSE_MS so
  // answered tabs don't blink indefinitely. A ref holds the pending timer so a new
  // turn (or unmount) can cancel a stale pulse.
  const [doneSessionId, setDoneSessionId] = useState<string | null>(null);
  const doneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The one user bubble currently playing its send entrance — identified by the
  // conversation it's in plus its index in that conversation's message list. Set
  // the instant a turn is sent and self-cleared after SEND_ANIM_MS so the "rise
  // into the thread" motion plays exactly once, never on a later re-render.
  const [sendingBubble, setSendingBubble] = useState<{ sessionId: string; index: number } | null>(
    null,
  );
  const sendAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // History rows mid-exit: a delete is deferred so the row can play its "pop"
  // animation before it's actually removed from the store. Ids collect here while
  // the motion runs; the store deletion + cleanup fire after LEAVE_ANIM_MS. A ref
  // mirrors the pending timers so unmount can clear them.
  const [leavingIds, setLeavingIds] = useState<string[]>([]);
  const leaveTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // A prompt handed in via the open event's `detail.prompt` (e.g. the quality
  // page's "Ask NORA" button). Held here and auto-sent by an effect once the
  // panel is open and idle — routing through state avoids capturing a stale
  // `send` closure in the window-event listener.
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  // The random sample of starter chips for the current empty state. Seeded once
  // and reshuffled each time the panel opens / a new chat starts.
  const [starters, setStarters] = useState<string[]>(pickStarters);
  // Whether the history drawer (all past conversations) is showing instead of the
  // active conversation.
  const [historyOpen, setHistoryOpen] = useState(false);
  // The session id the current stream is writing into. Kept in a ref so the async
  // SSE callbacks and stop()/finally always target the right conversation even if
  // the user switches tabs mid-stream; mirrored to state so we can bind the
  // in-panel streaming indicators to "is the streaming tab the one on screen".
  const streamSessionRef = useRef<string | null>(null);
  const [streamSessionId, setStreamSessionId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Holds the latest `stop` so callbacks defined BEFORE it (requestDelete) can
  // call the current implementation without a stale closure or hoist headache.
  const stopRef = useRef<(() => void) | null>(null);
  const navigate = useNavigate();

  // Session store: the active conversation, the open tabs, and full history.
  const activeSession = useChatSessionStore(selectActiveSession);
  const tabs = useChatSessionStore(selectOpenTabs);
  const history = useChatSessionStore(selectHistory);
  const activeId = useChatSessionStore((s) => s.activeId);
  // Actions are stable references in Zustand — safe to read once per render.
  const ensureActive = useChatSessionStore((s) => s.ensureActive);
  const newSession = useChatSessionStore((s) => s.newSession);
  const setActive = useChatSessionStore((s) => s.setActive);
  const openSession = useChatSessionStore((s) => s.openSession);
  const closeTab = useChatSessionStore((s) => s.closeTab);
  const deleteSession = useChatSessionStore((s) => s.deleteSession);
  const setSessionMessages = useChatSessionStore((s) => s.setMessages);

  // Stable reference: the store returns the same messages array until it changes,
  // so memoising on it keeps the empty-fallback identity steady across renders
  // (the scroll effect depends on this).
  const messages = useMemo(() => activeSession?.messages ?? [], [activeSession?.messages]);
  // The visual streaming indicators belong to the tab the stream is writing into;
  // on any other tab the panel reads as idle.
  const streamingHere = streaming && streamSessionId === activeId;

  // Open via the bezel event or Cmd/Ctrl+J; Escape closes. The open event may
  // carry an optional { detail: { prompt } } to auto-ask NORA a question (the
  // "Ask NORA" QC-verification button); the payload-less dispatch is unchanged.
  useEffect(() => {
    const openHandler = (e: Event) => {
      setOpen(true);
      setHistoryOpen(false);
      setStarters(pickStarters()); // fresh suggestions each open
      const prompt = (e as CustomEvent).detail?.prompt;
      if (typeof prompt === 'string' && prompt.trim()) setPendingPrompt(prompt);
    };
    const keyHandler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen((v) => {
          if (!v) setStarters(pickStarters()); // reshuffle when opening via ⌘J
          return !v;
        });
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('meridian:open-chat', openHandler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('meridian:open-chat', openHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  // Guarantee there's an active conversation to read/write whenever the panel is
  // open (creates a fresh session on first open / after a full clear).
  useEffect(() => {
    if (open) ensureActive();
  }, [open, ensureActive]);

  useEffect(() => {
    if (open && !historyOpen) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open, historyOpen]);

  // Auto-scroll to the newest content as it streams. Guarded because jsdom (tests)
  // and some environments don't implement Element.scrollTo.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [messages, toolStatus, activeId]);

  // Abort any in-flight request when the panel closes or unmounts.
  useEffect(() => {
    if (!open && abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    return () => abortRef.current?.abort();
  }, [open]);

  // Flash the green "done" pulse on a session's tab, cancelling any pending pulse
  // first, then auto-clear it after DONE_PULSE_MS. Stable (only setState + ref).
  const markDone = useCallback((sessionId: string) => {
    if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    setDoneSessionId(sessionId);
    doneTimerRef.current = setTimeout(() => {
      setDoneSessionId((cur) => (cur === sessionId ? null : cur));
      doneTimerRef.current = null;
    }, DONE_PULSE_MS);
  }, []);

  // Deferred delete: mark the row as leaving so it plays its "pop" exit, then
  // actually remove the conversation from the store after LEAVE_ANIM_MS. If the
  // deleted row owns the in-flight stream it's aborted first (same lock-out as
  // closing its tab). Guarded against a double-click on the same row. `onGone`
  // runs after the store delete so focus can move to a surviving neighbor.
  const requestDelete = useCallback(
    (id: string, onGone?: () => void) => {
      if (leaveTimersRef.current.has(id)) return; // already leaving
      if (streaming && id === streamSessionRef.current) stopRef.current?.();
      setLeavingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      const timer = setTimeout(() => {
        leaveTimersRef.current.delete(id);
        setLeavingIds((prev) => prev.filter((x) => x !== id));
        deleteSession(id);
        onGone?.();
      }, LEAVE_ANIM_MS);
      leaveTimersRef.current.set(id, timer);
    },
    [streaming, deleteSession],
  );

  // Cancel pending pulse / send-entrance / leave timers on unmount.
  useEffect(() => {
    const leaveTimers = leaveTimersRef.current;
    return () => {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
      if (sendAnimTimerRef.current) clearTimeout(sendAnimTimerRef.current);
      leaveTimers.forEach((t) => clearTimeout(t));
      leaveTimers.clear();
    };
  }, []);

  const send = useCallback(async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || streaming) return;

    // Resolve (and create if needed) the conversation this turn belongs to, and
    // pin the stream to it so tab switches don't misroute tokens.
    const sessionId = ensureActive();
    streamSessionRef.current = sessionId;
    setStreamSessionId(sessionId);

    // Tracks whether this turn reached the end cleanly (no error, no abort); read
    // in `finally` to decide whether to fire the green "done" pulse. `finally`
    // runs on every exit path, so a plain flag is the reliable success signal.
    let turnFailed = false;

    // A fresh turn supersedes any lingering "done" pulse on this tab (functional
    // update so `send` needn't depend on doneSessionId).
    setDoneSessionId((cur) => (cur === sessionId ? null : cur));
    setError(null);
    setErrorSessionId(null);
    setInput('');
    setHistoryOpen(false);

    // Build the wire history (only role+content) capped to the last N turns, read
    // fresh from the store, then append the new user turn.
    const prior = useChatSessionStore.getState().sessions.find((s) => s.id === sessionId)?.messages ?? [];
    const priorWire = prior.slice(-MAX_HISTORY_TURNS).map((m) => ({ role: m.role, content: m.content }));
    const wire = [...priorWire, { role: 'user' as const, content: text }];

    // Mark the about-to-be-appended user bubble so it plays the "rise into the
    // thread" entrance exactly once. Its index is the current message count (the
    // user turn is pushed onto the end); clear any prior marker's timer first.
    if (sendAnimTimerRef.current) clearTimeout(sendAnimTimerRef.current);
    let userIndex = 0;
    setSessionMessages(sessionId, (prev) => {
      userIndex = prev.length;
      return [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }];
    });
    setSendingBubble({ sessionId, index: userIndex });
    sendAnimTimerRef.current = setTimeout(() => {
      setSendingBubble((cur) =>
        cur && cur.sessionId === sessionId && cur.index === userIndex ? null : cur,
      );
      sendAnimTimerRef.current = null;
    }, SEND_ANIM_MS);
    setStreaming(true);
    setReceiving(false); // start in "thinking" until the first token/tool arrives

    // Best-effort DTN token for the live-observation tool; the server degrades
    // gracefully without it, so a token failure must not block the request.
    let authToken: string | undefined;
    try {
      authToken = await new ObservationsAPI().getAccessToken();
    } catch {
      authToken = undefined;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ messages: wire }),
        signal: controller.signal,
      });

      if (res.status === 503) {
        turnFailed = true;
        setError('NORA is currently unavailable.');
        setErrorSessionId(sessionId);
        setSessionMessages(sessionId, (prev) => prev.slice(0, -1)); // drop empty assistant bubble
        setStreaming(false);
        return;
      }
      if (!res.ok || !res.body) {
        throw new Error(`chat request failed: ${res.status}`);
      }

      await consumeSse(res.body, {
        onToken: (t) => {
          setReceiving(true); // tokens are flowing → hide the "thinking" indicator
          setSessionMessages(sessionId, (prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + t };
            return next;
          });
        },
        // A tool starts: show its chip and stop treating us as "receiving" so the
        // gap after the chip (and any pause before the next answer) reads as work.
        onToolStart: (name) => {
          setReceiving(false);
          setToolStatus(chipLabel(name));
        },
        onToolEnd: () => {
          setReceiving(false); // back to "thinking" until the next token arrives
          setToolStatus(null);
        },
        onError: (msg) => {
          turnFailed = true;
          setError(msg);
          setErrorSessionId(sessionId);
        },
      });
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        turnFailed = true;
        setError('Something went wrong talking to NORA.');
        setErrorSessionId(sessionId);
      } else {
        // A user-initiated Stop/abort is neither success nor failure — no pulse.
        turnFailed = true;
      }
    } finally {
      // Finalize the assistant bubble: split off the links + followups blocks.
      setSessionMessages(sessionId, (prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === 'assistant') {
          const afterLinks = parseLinks(last.content);
          const afterFollow = parseFollowups(afterLinks.text);
          next[next.length - 1] = {
            ...last,
            content: afterFollow.text,
            links: afterLinks.links,
            followups: afterFollow.followups,
          };
        }
        return next;
      });
      setToolStatus(null);
      setStreaming(false);
      setReceiving(false);
      abortRef.current = null;
      // Clean finish → brief green "done" pulse on this tab. Skipped on error or a
      // user Stop (turnFailed), where the dot goes straight back to idle/red.
      if (!turnFailed) markDone(sessionId);
    }
  }, [input, streaming, ensureActive, setSessionMessages, markDone]);

  // Auto-send a prompt that arrived via the open event, once the panel is open
  // and not mid-stream. Cleared before sending so it fires exactly once.
  useEffect(() => {
    if (open && pendingPrompt && !streaming) {
      const p = pendingPrompt;
      setPendingPrompt(null);
      void send(p);
    }
  }, [open, pendingPrompt, streaming, send]);

  // Stop a slow/unwanted generation mid-stream. Aborting the fetch unwinds
  // through consumeSse → the send() catch (AbortError is swallowed) → finally.
  // We also flip streaming off and finalize the partial bubble here directly, so
  // the UI returns to a sendable state immediately rather than waiting on the
  // reader to unwind (which, depending on the transport, may lag).
  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setToolStatus(null);
    setStreaming(false);
    setReceiving(false);
    const sessionId = streamSessionRef.current;
    if (!sessionId) return;
    setSessionMessages(sessionId, (prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        const { text: cleaned, links } = parseLinks(last.content);
        // Drop an empty assistant bubble (stopped before any token); otherwise
        // keep the partial text the user already saw.
        if (!cleaned && (!links || links.length === 0)) return next.slice(0, -1);
        next[next.length - 1] = { ...last, content: cleaned, links };
      }
      return next;
    });
  };
  // Keep the ref pointed at the current stop so requestDelete (defined earlier)
  // always calls the live implementation.
  stopRef.current = stop;

  const onLinkClick = (path: string) => {
    // Defense in depth: validate the code portion against the loaded catalog when
    // the path targets a station, so a hallucinated code can't navigate to a dead
    // route. Non-station canonical paths pass through.
    const stationMatch = path.match(/^\/station\/([^/?]+)/);
    if (stationMatch) {
      const code = decodeURIComponent(stationMatch[1]);
      const known = useStationDataStore.getState().allStations.some((s) => s.stationCode === code);
      if (!known) {
        setError(`Station "${code}" was not found in the catalog.`);
        setErrorSessionId(activeId);
        return;
      }
    }
    setOpen(false);
    navigate(path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Switch to a tab (or a history conversation): clear any stale error and leave
  // the history drawer.
  const goToSession = (id: string, via: 'tab' | 'history') => {
    setError(null);
    setErrorSessionId(null);
    setHistoryOpen(false);
    if (via === 'history') openSession(id);
    else setActive(id);
  };

  const startNewChat = () => {
    setError(null);
    setErrorSessionId(null);
    setHistoryOpen(false);
    newSession();
    setStarters(pickStarters());
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // "Thinking" = a turn is in flight but nothing is currently moving on screen:
  // no tool chip showing AND tokens aren't actively streaming. This covers EVERY
  // quiet gap, not just the first — the initial wait, the pause after a tool
  // finishes, and (critically) the multi-second gap while the model composes a
  // long answer after an earlier segment already streamed.
  const thinking = streamingHere && !toolStatus && !receiving;

  // True while the user is composing a message. Suggestion chips (empty-state
  // starters and post-answer follow-ups) hide when there's a draft so they don't
  // compete with what the user is typing.
  const hasDraft = input.trim().length > 0;

  // The blinking-dot status for a given conversation tab. Running wins over a
  // stale error/done (a re-asked tab is generating again); a live error outranks
  // the transient "done" pulse.
  const tabStatus = (id: string): TabStatus => {
    if (streaming && streamSessionId === id) return 'running';
    if (error && errorSessionId === id) return 'error';
    if (doneSessionId === id) return 'done';
    return 'idle';
  };
  const STATUS_LABEL: Record<Exclude<TabStatus, 'idle'>, string> = {
    running: 'NORA is running',
    done: 'NORA finished',
    error: 'NORA hit an error',
  };

  if (!open) return null;

  return (
    <div className="chat-panel-overlay" role="presentation" onClick={() => setOpen(false)}>
      <div
        className="chat-panel"
        role="dialog"
        /* Intentionally NOT aria-modal: the overlay is transparent + pointer-events:
           none so the page stays fully interactive and Escape only minimizes. Marking
           it modal would tell assistive tech the rest of the app is inert (confining
           the SR cursor to the panel) while sighted users keep using the page. */
        aria-label="NORA — Station Operator chat"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-panel-header">
          <span className="chat-panel-title">
            NORA
            <span className="chat-panel-subtitle">Station Operator</span>
          </span>
          <div className="chat-panel-header-actions">
            <button
              type="button"
              className={`chat-panel-icon-btn${historyOpen ? ' is-active' : ''}`}
              aria-label="Chat history"
              aria-pressed={historyOpen}
              title="History"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 8v4l2.5 2.5M3.5 12a8.5 8.5 0 1 0 2.2-5.7M3.5 4v3h3"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="chat-panel-close chat-panel-icon-btn"
              aria-label="Minimize chat"
              title="Minimize (Esc)"
              onClick={() => setOpen(false)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab strip — the open conversations. Always shown so the multi-session
            model (and the trailing "+") is discoverable. These are browser-style
            conversation tabs (each independently focusable, with its own close
            button) — NOT a WAI-ARIA tabpanel widget — so we use group/aria-current
            rather than tablist/tab, which would promise arrow-key roving + an
            associated tabpanel that this UI doesn't implement. */}
        <div className="chat-tabs" role="group" aria-label="Open conversations">
          {tabs.map((t) => {
            const isActive = t.id === activeId;
            const status = tabStatus(t.id);
            // A conversation deleted from the history drawer is removed from the
            // store only after LEAVE_ANIM_MS (see requestDelete). If it's also open
            // as a tab, drive the SAME "pop + collapse" exit here off leavingIds so
            // the tab and the history row animate away in lockstep instead of the
            // tab blinking out the instant the store deletion lands.
            const leaving = leavingIds.includes(t.id);
            return (
              <div
                key={t.id}
                className={`chat-tab${isActive ? ' is-active' : ''}${leaving ? ' is-leaving' : ''}`}
              >
                {status !== 'idle' && (
                  <span
                    className={`chat-tab-status chat-tab-status--${status}`}
                    role="status"
                    aria-label={STATUS_LABEL[status]}
                    title={STATUS_LABEL[status]}
                  />
                )}
                <button
                  type="button"
                  aria-current={isActive ? 'true' : undefined}
                  className="chat-tab-label"
                  title={t.title}
                  onMouseEnter={primeTabMarquee}
                  onClick={() => goToSession(t.id, 'tab')}
                >
                  {/* Inner span is the marquee track: it slides left on hover to
                      reveal a title too long for the clipped button. */}
                  <span className="chat-tab-label-text">{t.title}</span>
                </button>
                <button
                  type="button"
                  className="chat-tab-close"
                  aria-label={`Close ${t.title}`}
                  title="Close tab"
                  onClick={(e) => {
                    // If this tab owns the in-flight stream, abort it first —
                    // otherwise Stop becomes unreachable (streamingHere goes false
                    // on every remaining tab) while global `streaming` keeps Send
                    // disabled everywhere until the backend finishes on its own.
                    if (streaming && t.id === streamSessionRef.current) stop();
                    // Focus would fall to <body> when this button unmounts; move it
                    // to the newly-active tab (or the input) so keyboard order holds.
                    const strip = e.currentTarget.closest('.chat-tabs');
                    closeTab(t.id);
                    requestAnimationFrame(() => {
                      const nextTab = strip?.querySelector<HTMLElement>('.chat-tab.is-active .chat-tab-label');
                      (nextTab ?? inputRef.current)?.focus();
                    });
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            );
          })}
          {/* New-chat "+" lives in the tab strip, right beside the tabs — a browser
              "new tab" affordance, not a header action. */}
          <button
            type="button"
            className="chat-tab-new"
            aria-label="New chat"
            title="New chat"
            onClick={startNewChat}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {historyOpen ? (
          <div className="chat-history" role="region" aria-label="Conversation history">
            {history.length === 0 ? (
              <p className="chat-history-empty">No conversations yet.</p>
            ) : (
              <ul className="chat-history-list">
                {history.map((h) => {
                  const leaving = leavingIds.includes(h.id);
                  return (
                  <li
                    key={h.id}
                    className={`chat-history-row${h.id === activeId ? ' is-active' : ''}${leaving ? ' is-leaving' : ''}`}
                  >
                    <button
                      type="button"
                      className="chat-history-open"
                      onClick={() => goToSession(h.id, 'history')}
                      // While a row is popping out it must not be re-openable.
                      disabled={leaving}
                    >
                      <span className="chat-history-title">{h.title}</span>
                      <span className="chat-history-meta">
                        {h.messages.length} {h.messages.length === 1 ? 'message' : 'messages'} · {relTime(h.updatedAt)}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="chat-history-delete"
                      aria-label={`Delete ${h.title}`}
                      title="Delete conversation"
                      disabled={leaving}
                      onClick={(e) => {
                        // Deferred delete: the row plays its "pop" exit, THEN the
                        // conversation is removed from the store (requestDelete also
                        // aborts the stream if this row owns it — delete is
                        // unrecoverable). Capture the neighbor now so focus can move
                        // into the drawer once the row is gone.
                        const li = e.currentTarget.closest('.chat-history-row');
                        const neighbor = (li?.nextElementSibling ?? li?.previousElementSibling) as HTMLElement | null;
                        requestDelete(h.id, () => {
                          requestAnimationFrame(() => {
                            neighbor?.querySelector<HTMLElement>('.chat-history-open')?.focus();
                          });
                        });
                      }}
                    >
                      {/* Trash icon split into a LID (rim + handle) and a CAN
                          (body + grips) so the lid can hinge open on hover/press
                          — the pivot is the rim's left end (see .chat-trash-lid). */}
                      <svg
                        className="chat-trash"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <g className="chat-trash-lid">
                          <path
                            d="M4 7h16"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                          <path
                            d="M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <path
                          className="chat-trash-can"
                          d="M6 7v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7M10 11v6M14 11v6"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : (
          <div className="chat-panel-messages" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="chat-panel-empty">
                <p>
                  Hi, I’m NORA — your station operator. Ask about a station, filter the fleet, or check
                  coverage and quality.
                </p>
                {!hasDraft && (
                  <div className="chat-followups chat-followups--starters">
                    {starters.map((p, j) => (
                      <button key={j} type="button" className="chat-followup-chip" onClick={() => send(p)}>
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {messages.map((m, i) => {
              // Play the send entrance only on the exact user bubble that was just
              // sent, in this conversation — cleared after SEND_ANIM_MS so it never
              // replays on a re-render, tab switch, or reload.
              const isSending =
                m.role === 'user' &&
                sendingBubble?.sessionId === activeId &&
                sendingBubble?.index === i;
              return (
                <div key={i} className={`chat-msg chat-msg--${m.role}${isSending ? ' is-sending' : ''}`}>
                  {m.content && (
                    <div className="chat-msg-bubble">
                      {/* Assistant text is Markdown (tables/lists/bold); user text stays literal. */}
                      {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
                    </div>
                  )}
                  {m.links && m.links.length > 0 && (
                    <div className="chat-msg-links">
                      {m.links.map((l, j) => (
                        <button key={j} type="button" className="chat-link-btn" onClick={() => onLinkClick(l.path)}>
                          {l.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Suggested follow-ups are intentionally NOT rendered per-answer:
                      prompt suggestions belong to a fresh conversation's empty state
                      (the starter chips above), not appended to every existing answer.
                      The ```followups block is still parsed out of the text so it
                      never shows raw; we simply don't surface it as chips. */}
                </div>
              );
            })}
            {error && errorSessionId === activeId && (
              <div className="chat-panel-error" role="alert">
                {error}
              </div>
            )}
          </div>
        )}

        {!historyOpen && (
          <div className="chat-panel-composer">
            {/* Activity indicator — bubble-less, pinned just above the composer so
                "thinking"/"analyzing" reads as a status line, not a chat message.
                At most one shows: a tool chip while a tool runs, otherwise the
                thinking dots during any quiet gap. */}
            {streamingHere && toolStatus && (
              <div className="chat-activity" role="status">
                <span className="chat-tool-spinner" aria-hidden="true" />
                <span className="chat-activity-label">{toolStatus}</span>
              </div>
            )}
            {thinking && (
              <div className="chat-activity" role="status" aria-label="NORA is thinking">
                <span className="chat-activity-label">NORA is thinking</span>
                <span className="chat-thinking-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            )}
            <div className="chat-panel-input-row">
            <input
              ref={inputRef}
              className="chat-panel-input"
              type="text"
              placeholder="Ask NORA…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={streamingHere}
              aria-label="Message"
              /* Defense-in-depth: the server independently caps message length
                 (4000 chars) and history size; this just gives immediate UX feedback. */
              maxLength={2000}
            />
            {streamingHere ? (
              <button
                type="button"
                className="chat-panel-send chat-panel-stop"
                onClick={stop}
                aria-label="Stop generating"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className="chat-panel-send"
                onClick={() => send()}
                disabled={!input.trim() || streaming}
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 12l15-7-6 15-2.5-5.5L4 12z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
